import { useMutation } from '@apollo/client';
import { gql } from '__generated__/gql';
import { FC } from 'react';

import { FormInput, FormPanelWithReadMode } from 'components/FormPanel';

import CopyToClipboard from 'primitives/CopyToClipboard';
import { Dialog, DialogPanel, DialogTitle } from 'primitives/Dialog';

const UPDATE_DEAL_INVITE_LINK_STATUS = gql(`
  mutation UpdateDealInviteLink($id: ID!, $status: DealInviteLinkStatusEnumType!) {
    updateDealInviteLinkStatus(id: $id, status: $status) {
      id
      status
    }
  }
`);

type DealInviteLink = {
  id: string;
  status: 'ACTIVE' | 'INACTIVE';
  minimumInvestmentAmount: number;
  maximumInvestmentAmount?: number;
  numberOfInvestors?: number;
  note?: string;
  shortUrl: string;
  createdAt: string;
};

const UpdateDealInviteLinkStatus: FC<{ inviteLink: DealInviteLink; onClose: () => void }> = ({
  inviteLink,
  onClose,
}) => {
  const [updateDealInviteLinkStatus, { loading, error }] = useMutation(
    UPDATE_DEAL_INVITE_LINK_STATUS
  );

  return (
    <Dialog open={true} onClose={onClose}>
      <DialogPanel>
        <DialogTitle>Update Deal Invite Link Status</DialogTitle>
        <div className="mb-4 bg-gray-50 p-4 rounded-md">
          <div className="mb-4">
            <span className="block text-sm font-medium text-gray-500 mb-1">Short URL</span>
            <div className="flex items-center justify-between bg-white border border-gray-200 rounded p-2">
              <span
                className="text-sm text-gray-900 truncate mr-2 select-all"
                title={inviteLink.shortUrl}
              >
                {inviteLink.shortUrl}
              </span>
              <CopyToClipboard value={inviteLink.shortUrl} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="block text-sm font-medium text-gray-500">Min Investment</span>
              <span className="block text-sm text-gray-900">
                ₹{inviteLink.minimumInvestmentAmount.toLocaleString()}
              </span>
            </div>
            <div>
              <span className="block text-sm font-medium text-gray-500">Max Investment</span>
              <span className="block text-sm text-gray-900">
                {inviteLink.maximumInvestmentAmount
                  ? `₹${inviteLink.maximumInvestmentAmount.toLocaleString()}`
                  : '-'}
              </span>
            </div>
            <div className="col-span-2">
              <span className="block text-sm font-medium text-gray-500">Note</span>
              <span className="block text-sm text-gray-900">{inviteLink.note || '-'}</span>
            </div>
          </div>
        </div>

        <FormPanelWithReadMode
          loading={loading}
          error={error}
          onSubmit={data => {
            updateDealInviteLinkStatus({
              variables: {
                id: inviteLink.id,
                status: data.status,
              },
            }).then(() => {
              onClose();
            });
          }}
        >
          <FormInput
            type="select"
            fieldName="status"
            label="Status"
            defaultValue={inviteLink.status}
            options={[
              { value: 'ACTIVE', label: 'Active', color: 'bg-green-500' },
              { value: 'INACTIVE', label: 'Inactive', color: 'bg-red-500' },
            ]}
          />
        </FormPanelWithReadMode>
      </DialogPanel>
    </Dialog>
  );
};

export default UpdateDealInviteLinkStatus;
